import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { listingSearchParam } from 'apis/handler/listing/listingSchema';

import type { MapPointsParam } from '../model/dto';
import { getListing, getListingMapPreview, getMapPoints, getSameTypeRooms, getSimilarListings } from './service';

export const getListingQueryKey = (searchParam: listingSearchParam) => {
  return ['listing', searchParam];
};

export const useGetListing = ({
  searchParam,
  enabled = true,
}: {
  searchParam: listingSearchParam;
  enabled?: boolean;
}) => {
  return useInfiniteQuery({
    queryKey: getListingQueryKey(searchParam),
    queryFn: ({ pageParam }) => getListing(searchParam, { page: pageParam, size: 10 }),
    initialPageParam: 0,
    // 서버가 결과 없이 실패를 돌려준 경우(예: 깨진 로그인 토큰)에도 페이지 전체가 죽지 않게 방어
    getNextPageParam: (lastPage) => (lastPage?.sliceInfo?.hasNext ? lastPage.sliceInfo.pageNumber + 1 : undefined),
    placeholderData: keepPreviousData,
    // 대학 랜딩의 미등록 슬러그처럼 파라미터가 무효일 때 낭비 요청을 막는 스위치 (기본 true — 기존 호출부 무영향)
    enabled,
  });
};

export const useGetListingMapPreview = (id: number | null) => {
  return useQuery({
    queryKey: ['listing', 'map-preview', id],
    queryFn: () => getListingMapPreview(id!),
    enabled: id !== null,
  });
};

export const useGetMapPoints = ({ params }: { params: MapPointsParam | null }) => {
  return useQuery({
    queryKey: ['listing', 'map-points', params],
    queryFn: () => getMapPoints(params!),
    enabled: params !== null,
    placeholderData: keepPreviousData,
  });
};

export const SIMILAR_LISTINGS_QUERY_KEY = ['listing', 'similar'];
export const SAME_TYPE_ROOMS_QUERY_KEY = ['listing', 'same-type-rooms'];

export const useGetSameTypeRooms = (id: string) => {
  return useQuery({
    queryKey: [...SAME_TYPE_ROOMS_QUERY_KEY, id],
    queryFn: () => getSameTypeRooms(id),
    // 부가 칸이라 서버 장애여도 상세 페이지 전체를 에러 화면으로 바꾸지 않는다 (실패 시 칸만 숨김)
    throwOnError: false,
  });
};

export const useGetSimilarListings = (id: string) => {
  return useQuery({
    queryKey: [...SIMILAR_LISTINGS_QUERY_KEY, id],
    queryFn: () => getSimilarListings(id),
    // 데스크톱 상세는 탭 표시 여부를 보려고 먼저 구독하고, 위젯이 뒤에 같은 키로 구독한다.
    // staleTime 0 이면 뒤 구독자가 다시 요청해 상세 한 번에 두 번 나간다 — 잠깐은 같은 응답을 쓴다.
    // 길게 두면 이 화면에서 로그인한 뒤 카드 하트(찜 여부)가 늦게 바뀌므로 짧게 둔다.
    staleTime: 30 * 1000,
    // 부가 섹션이라 서버 장애여도 상세 페이지 전체를 에러 화면으로 바꾸지 않는다 (실패 시 섹션만 숨김)
    throwOnError: false,
  });
};
